package com.havn.app.features.followups.presentation

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.havn.app.core.designsystem.EmptyState
import com.havn.app.core.designsystem.HavnBackground
import com.havn.app.core.designsystem.HavnButton
import com.havn.app.core.designsystem.HavnButtonStyle
import com.havn.app.core.designsystem.HavnCard
import com.havn.app.core.designsystem.HavnCardBorder
import com.havn.app.core.designsystem.HavnDanger
import com.havn.app.core.designsystem.HavnGold
import com.havn.app.core.designsystem.HavnIcon
import com.havn.app.core.designsystem.HavnIconKey
import com.havn.app.core.designsystem.HavnIvory
import com.havn.app.core.designsystem.HavnMetric
import com.havn.app.core.designsystem.HavnMuted
import com.havn.app.core.designsystem.HavnPushNavigation
import com.havn.app.core.designsystem.HavnScreenFrame
import com.havn.app.core.designsystem.HavnSkeletonBlock
import com.havn.app.core.designsystem.HavnSpacing
import com.havn.app.core.designsystem.HavnSurfaceElevated
import com.havn.app.core.designsystem.HavnTextField
import com.havn.app.core.designsystem.LoadingState
import com.havn.app.core.localization.LocalHavnStrings
import com.havn.app.core.media.HavnDatabaseImage
import com.havn.app.core.media.havnPropertyPhotoPreviewSource
import com.havn.app.features.attachments.domain.HavnAttachment
import com.havn.app.features.followups.domain.FollowUp
import com.havn.app.features.followups.domain.FollowUpChannel
import com.havn.app.features.followups.domain.FollowUpStatus
import com.havn.app.features.leads.domain.Lead
import com.havn.app.ui.theme.LocalHavnThemeTokens

@Composable
fun FollowUpsScreen(
    viewModel: FollowUpsViewModel,
    onBackToLeadDetail: () -> Unit = {},
    onFollowUpCompleted: () -> Unit = {},
    canUseAiFollowUps: Boolean = true,
    onAiUpgradeRequired: () -> Unit = {}
) {
    val state by viewModel.uiState.collectAsState()
    val strings = LocalHavnStrings.current
    val completedFollowUpEventId = state.completedFollowUpEventId

    LaunchedEffect(completedFollowUpEventId) {
        if (completedFollowUpEventId != null) {
            onFollowUpCompleted()
            viewModel.consumeCompletedFollowUpEvent()
        }
    }

    HavnPushNavigation(
        targetState = state.mode,
        depthFor = FollowUpsMode::navigationDepth
    ) { mode ->
        HavnScreenFrame(
            eyebrow = "",
            title = when (mode) {
                FollowUpsMode.Queue -> "Follow-ups"
                FollowUpsMode.Generate -> "Send Follow Up"
                FollowUpsMode.Preview -> "Preview follow-up"
            },
            subtitle = when (mode) {
                FollowUpsMode.Queue -> "Create polished SMS and email drafts from lead context."
                FollowUpsMode.Generate -> ""
                FollowUpsMode.Preview -> "Review the generated draft before saving."
            },
            metrics = if (mode == FollowUpsMode.Queue) {
                listOf(
                    HavnMetric("Drafts", state.pendingCount.toString(), "pending"),
                    HavnMetric("Sent", state.sentCount.toString(), "manual"),
                    HavnMetric("Leads", state.leads.size.toString(), "available")
                )
            } else {
                emptyList()
            },
            backLabel = when (mode) {
                FollowUpsMode.Queue -> null
                FollowUpsMode.Generate -> "Lead"
                FollowUpsMode.Preview -> "Generate Draft"
            },
            onBack = when (mode) {
                FollowUpsMode.Queue -> null
                FollowUpsMode.Generate -> onBackToLeadDetail
                FollowUpsMode.Preview -> viewModel::openGenerator
            },
            isRefreshing = state.isLoading,
            onRefresh = viewModel::refresh
        ) {
            Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
                state.error?.let { message ->
                    HavnCard {
                        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
                            Text(message, color = HavnDanger, style = MaterialTheme.typography.bodyLarge)
                            if (message.isUpgradePrompt()) {
                                HavnButton(
                                    text = strings.upgradePlan,
                                    onClick = onAiUpgradeRequired,
                                    style = HavnButtonStyle.Primary,
                                    modifier = Modifier.fillMaxWidth()
                                )
                            }
                            HavnButton(
                                text = strings.commonDismiss,
                                onClick = viewModel::clearError,
                                style = HavnButtonStyle.Ghost,
                                modifier = Modifier.fillMaxWidth()
                            )
                        }
                    }
                }

                when (mode) {
                    FollowUpsMode.Queue -> FollowUpQueueScreen(state, viewModel)
                    FollowUpsMode.Generate -> FollowUpComposerContent(
                        state = state,
                        viewModel = viewModel,
                        canUseAiAssistant = canUseAiFollowUps,
                        onAiUpgradeRequired = onAiUpgradeRequired
                    )
                    FollowUpsMode.Preview -> FollowUpPreviewScreen(state, viewModel)
                }
            }
        }
    }
}

@Composable
fun FollowUpComposerContent(
    state: FollowUpsUiState,
    viewModel: FollowUpsViewModel,
    showLeadSummary: Boolean = true,
    showAiAssistant: Boolean = true,
    canUseAiAssistant: Boolean = true,
    onAiUpgradeRequired: () -> Unit = {}
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    val selectedLead = state.selectedLead
    val isInquiry = selectedLead?.isInquiry == true
    Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
        if (showLeadSummary) selectedLead?.let { lead ->
            HavnCard {
                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    Text(lead.fullName, color = theme.primaryText, style = MaterialTheme.typography.headlineSmall)
                    Text(lead.sourceProperty.title, color = theme.secondaryText, style = MaterialTheme.typography.bodyLarge)
                    Text(lead.contactLine, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
                }
            }
        }

        HavnCard {
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                Text(if (isInquiry) strings.inquiryFollowUp else strings.leadFollowUp, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
                Column(verticalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                        MessageTypeChip(strings.followUpMessageTypeThankYou, selected = state.selectedMessageType == FollowUpMessageType.ThankYou, modifier = Modifier.weight(1f)) {
                            viewModel.selectMessageType(FollowUpMessageType.ThankYou)
                        }
                        MessageTypeChip(strings.followUpMessageTypePropertyInfo, selected = state.selectedMessageType == FollowUpMessageType.PropertyInfo, modifier = Modifier.weight(1f)) {
                            viewModel.selectMessageType(FollowUpMessageType.PropertyInfo)
                        }
                    }
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                        MessageTypeChip(strings.followUpMessageTypeFollowUp, selected = state.selectedMessageType == FollowUpMessageType.FollowUp, modifier = Modifier.weight(1f)) {
                            viewModel.selectMessageType(FollowUpMessageType.FollowUp)
                        }
                        MessageTypeChip(strings.followUpMessageTypeCheckIn, selected = state.selectedMessageType == FollowUpMessageType.CheckIn, modifier = Modifier.weight(1f)) {
                            viewModel.selectMessageType(FollowUpMessageType.CheckIn)
                        }
                    }
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                        MessageTypeChip(strings.followUpMessageTypePriceUpdate, selected = state.selectedMessageType == FollowUpMessageType.PriceUpdate, modifier = Modifier.weight(1f)) {
                            viewModel.selectMessageType(FollowUpMessageType.PriceUpdate)
                        }
                        MessageTypeChip(strings.followUpMessageTypePropertyBrochure, selected = state.selectedMessageType == FollowUpMessageType.PropertyBrochure, modifier = Modifier.weight(1f)) {
                            viewModel.selectMessageType(FollowUpMessageType.PropertyBrochure)
                        }
                    }
                }
                if (state.showFileSelection) {
                    FollowUpFileSelector(
                        photos = state.availablePhotoDataUrls,
                        selectedPhotos = state.selectedPhotoDataUrls,
                        files = state.availablePropertyFiles,
                        selectedFileIds = state.selectedPropertyFileIds,
                        onTogglePhoto = viewModel::toggleSelectedPhoto,
                        onToggleFile = viewModel::toggleSelectedPropertyFile
                    )
                }
                if (showAiAssistant) {
                    AiAssistantCard(
                        isGenerating = state.isGenerating,
                        canGenerate = canUseAiAssistant,
                        onGenerate = if (canUseAiAssistant) {
                            viewModel::generateInlineDraft
                        } else {
                            onAiUpgradeRequired
                        }
                    )
                }
            }
        }

        HavnCard {
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                Text(strings.followUpPreview, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
                HavnTextField(
                    value = state.emailSubject,
                    onValueChange = viewModel::updatePreviewSubject,
                    label = strings.followUpSubject,
                    leadingIcon = HavnIconKey.Mail
                )
                HavnTextField(
                    value = state.sendFollowUpPreview,
                    onValueChange = viewModel::updatePreviewBody,
                    label = strings.followUpMessage,
                    singleLine = false
                )
            }
        }

        HavnButton(
            text = strings.followUpSendViaEmail,
            onClick = viewModel::sendSelectedDraftEmail,
            loading = state.savingChannel == FollowUpChannel.Email,
            modifier = Modifier.fillMaxWidth(),
            leadingIcon = HavnIconKey.Mail
        )
        if (!state.selectedLead?.phone.isNullOrBlank()) {
            HavnButton(
                text = strings.followUpSendViaSms,
                onClick = viewModel::sendSelectedDraftSms,
                loading = state.savingChannel == FollowUpChannel.Sms,
                modifier = Modifier.fillMaxWidth(),
                style = HavnButtonStyle.Secondary,
                leadingIcon = HavnIconKey.Message
            )
        }

        state.error?.let { message ->
            HavnCard {
                Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
                    Text(message, color = HavnDanger, style = MaterialTheme.typography.bodyLarge)
                    if (message.isUpgradePrompt()) {
                        HavnButton(
                            text = strings.upgradePlan,
                            onClick = onAiUpgradeRequired,
                            modifier = Modifier.fillMaxWidth(),
                            style = HavnButtonStyle.Primary
                        )
                    }
                    HavnButton(
                        text = strings.commonDismiss,
                        onClick = viewModel::clearError,
                        modifier = Modifier.fillMaxWidth(),
                        style = HavnButtonStyle.Ghost
                    )
                }
            }
        }
    }
}

private fun String.isUpgradePrompt(): Boolean {
    val normalized = lowercase()
    return "upgrade" in normalized || "current plan" in normalized || "limit reached" in normalized || "storage limit" in normalized || "ai " in normalized || "ai-" in normalized
}

@Composable
private fun FollowUpFileSelector(
    photos: List<String>,
    selectedPhotos: List<String>,
    files: List<HavnAttachment>,
    selectedFileIds: List<String>,
    onTogglePhoto: (String) -> Unit,
    onToggleFile: (String) -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    val distinctPhotos = photos.distinct()
    val selectedCount = selectedPhotos.distinct().size + selectedFileIds.distinct().size
    val totalCount = distinctPhotos.size + files.size
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text("Files", color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            Text(
                "$selectedCount/$totalCount",
                color = theme.secondaryText,
                style = MaterialTheme.typography.labelLarge
            )
        }
        LazyRow(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            itemsIndexed(distinctPhotos, key = { _, photo -> photo }) { index, photo ->
                val selected = photo in selectedPhotos
                Surface(
                    modifier = Modifier
                        .size(72.dp)
                        .clickable { onTogglePhoto(photo) },
                    shape = RoundedCornerShape(8.dp),
                    color = theme.surface,
                    border = BorderStroke(
                        width = if (selected) 2.dp else 1.dp,
                        color = if (selected) theme.primaryAccent else theme.border
                    )
                ) {
                    Box(contentAlignment = Alignment.Center) {
                        val rendered = HavnDatabaseImage(
                            dataUrl = photo,
                            previewDataUrl = havnPropertyPhotoPreviewSource(photo, 120),
                            contentDescription = "${strings.photo} ${index + 1}",
                            modifier = Modifier.fillMaxSize(),
                            contentScale = ContentScale.Crop
                        )
                        if (!rendered) {
                            HavnSkeletonBlock(Modifier.fillMaxSize(), shape = MaterialTheme.shapes.medium)
                            HavnIcon(HavnIconKey.Home, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(20.dp))
                        }
                        if (selected) {
                            Surface(
                                modifier = Modifier
                                    .align(Alignment.TopEnd)
                                    .padding(6.dp)
                                    .size(22.dp),
                                shape = RoundedCornerShape(11.dp),
                                color = theme.primaryAccent,
                                contentColor = theme.background
                            ) {
                                Box(contentAlignment = Alignment.Center) {
                                    HavnIcon(HavnIconKey.Check, contentDescription = null, tint = theme.background, modifier = Modifier.size(14.dp))
                                }
                            }
                        }
                    }
                }
            }
            itemsIndexed(files, key = { _, file -> file.id }) { _, file ->
                val selected = file.id in selectedFileIds
                Surface(
                    modifier = Modifier
                        .size(width = 126.dp, height = 72.dp)
                        .clickable { onToggleFile(file.id) },
                    shape = RoundedCornerShape(14.dp),
                    color = theme.surface,
                    border = BorderStroke(
                        width = if (selected) 2.dp else 1.dp,
                        color = if (selected) theme.primaryAccent else theme.border
                    )
                ) {
                    Box {
                        Row(
                            modifier = Modifier
                                .fillMaxSize()
                                .padding(8.dp),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            HavnIcon(HavnIconKey.Note, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(22.dp))
                            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                                Text(
                                    file.fileName,
                                    color = theme.primaryText,
                                    style = MaterialTheme.typography.labelMedium,
                                    maxLines = 2,
                                    overflow = TextOverflow.Ellipsis
                                )
                                Text(
                                    file.sizeLabel(),
                                    color = theme.secondaryText,
                                    style = MaterialTheme.typography.labelSmall,
                                    maxLines = 1,
                                    overflow = TextOverflow.Ellipsis
                                )
                            }
                        }
                        if (selected) {
                            Surface(
                                modifier = Modifier
                                    .align(Alignment.TopEnd)
                                    .padding(6.dp)
                                    .size(22.dp),
                                shape = RoundedCornerShape(11.dp),
                                color = theme.primaryAccent,
                                contentColor = theme.background
                            ) {
                                Box(contentAlignment = Alignment.Center) {
                                    HavnIcon(HavnIconKey.Check, contentDescription = null, tint = theme.background, modifier = Modifier.size(14.dp))
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun AiAssistantCard(
    isGenerating: Boolean,
    canGenerate: Boolean,
    onGenerate: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(14.dp),
        color = Color.Transparent,
        border = BorderStroke(1.dp, theme.primaryAccent.copy(alpha = 0.72f))
    ) {
        Box(
            modifier = Modifier
                .background(
                    Brush.horizontalGradient(
                        listOf(
                            theme.surface.copy(alpha = 0.88f),
                            theme.card.copy(alpha = 0.78f),
                            theme.primaryAccent.copy(alpha = 0.12f)
                        )
                    )
                )
                .padding(16.dp)
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(14.dp)
            ) {
                Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
                        HavnIcon(HavnIconKey.Star, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(22.dp))
                        Text(
                            strings.followUpAiAssistantTitle,
                            color = theme.primaryText,
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = FontWeight.SemiBold
                        )
                    }
                    Text(
                        strings.followUpAiAssistantBody,
                        color = theme.secondaryText,
                        style = MaterialTheme.typography.bodyMedium
                    )
                    HavnButton(
                        text = if (canGenerate) strings.followUpGenerateWithAi else strings.followUpUpgradeForAi,
                        onClick = onGenerate,
                        loading = isGenerating,
                        leadingIcon = HavnIconKey.Send
                    )
                }
                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(6.dp)
                ) {
                    HavnIcon(HavnIconKey.Star, contentDescription = null, tint = theme.primaryAccent.copy(alpha = 0.92f), modifier = Modifier.size(28.dp))
                    HavnIcon(HavnIconKey.Star, contentDescription = null, tint = theme.primaryAccent.copy(alpha = 0.58f), modifier = Modifier.size(48.dp))
                    HavnIcon(HavnIconKey.Star, contentDescription = null, tint = theme.primaryAccent.copy(alpha = 0.78f), modifier = Modifier.size(24.dp))
                }
            }
        }
    }
}

@Composable
private fun MessageTypeChip(
    text: String,
    selected: Boolean,
    modifier: Modifier = Modifier,
    onClick: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val shape = RoundedCornerShape(8.dp)
    Surface(
        modifier = modifier
            .height(52.dp)
            .clip(shape)
            .clickable(onClick = onClick),
        shape = shape,
        color = if (selected) Color.Transparent else theme.surface.copy(alpha = 0.62f),
        border = BorderStroke(1.dp, if (selected) theme.primaryAccent else theme.border)
    ) {
        Box(
            contentAlignment = Alignment.Center,
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = 8.dp)
        ) {
            Text(
                text = text,
                color = theme.primaryText,
                style = MaterialTheme.typography.labelSmall,
                fontWeight = FontWeight.Medium,
                textAlign = androidx.compose.ui.text.style.TextAlign.Center,
                maxLines = 2
            )
        }
    }
}

@Composable
private fun FollowUpQueueScreen(
    state: FollowUpsUiState,
    viewModel: FollowUpsViewModel
) {
    HavnButton(
        text = "Write follow-up",
        onClick = viewModel::openGenerator,
        modifier = Modifier.fillMaxWidth(),
        leadingIcon = HavnIconKey.Plus
    )

    if (state.isLoading) {
        repeat(4) {
            FollowUpSkeletonRow()
        }
    } else if (state.followUps.isEmpty()) {
        EmptyState(
            title = "No follow-ups yet",
            body = "Generate a draft from lead context, save it, then mark it sent when you send it manually.",
            actionText = "Create draft",
            onAction = viewModel::openGenerator
        )
    } else {
        HavnTextField(
            value = state.form.completedAt,
            onValueChange = { value -> viewModel.updateForm { copy(completedAt = value) } },
            label = "Sent at",
            placeholder = "2026-05-19T12:00:00Z",
            leadingIcon = HavnIconKey.Calendar
        )
        state.followUps.forEach { followUp ->
            FollowUpQueueRow(
                followUp = followUp,
                onMarkSent = { viewModel.markSent(followUp.id, state.form.completedAt) },
                enabled = !state.isSaving
            )
        }
    }
}

@Composable
private fun FollowUpSkeletonRow() {
    HavnCard(border = BorderStroke(1.dp, HavnCardBorder)) {
        Row(
            modifier = Modifier.padding(12.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            HavnSkeletonBlock(Modifier.size(36.dp), shape = RoundedCornerShape(18.dp))
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(7.dp)) {
                HavnSkeletonBlock(Modifier.fillMaxWidth(0.52f).height(14.dp))
                HavnSkeletonBlock(Modifier.fillMaxWidth(0.78f).height(11.dp))
            }
            HavnSkeletonBlock(Modifier.size(28.dp), shape = RoundedCornerShape(14.dp))
        }
    }
}

@Composable
fun GenerateFollowUpScreen(
    state: FollowUpsUiState,
    viewModel: FollowUpsViewModel
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    val isInquiry = state.selectedLead?.isInquiry == true
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text("Choose lead", color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
            if (state.leads.isEmpty()) {
                Text("No leads available yet.", color = theme.secondaryText, style = MaterialTheme.typography.bodyLarge)
            } else {
                state.leads.forEach { lead ->
                    LeadPickerRow(
                        lead = lead,
                        selected = state.selectedLeadId == lead.id,
                        onClick = { viewModel.selectLead(lead.id) }
                    )
                }
            }
        }
    }

    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            HavnTextField(
                value = state.form.visitorName,
                onValueChange = { value -> viewModel.updateForm { copy(visitorName = value) } },
                label = "Lead name",
                leadingIcon = HavnIconKey.User
            )
            HavnTextField(
                value = state.form.propertyAddress,
                onValueChange = { value -> viewModel.updateForm { copy(propertyAddress = value) } },
                label = "Property address",
                leadingIcon = HavnIconKey.Properties
            )
            HavnTextField(
                value = state.form.visitorInterest,
                onValueChange = { value -> viewModel.updateForm { copy(visitorInterest = value) } },
                label = "Lead interest",
                placeholder = "Buying, investing, relocating",
                leadingIcon = HavnIconKey.Search
            )
            HavnTextField(
                value = state.form.leadStatus,
                onValueChange = { value -> viewModel.updateForm { copy(leadStatus = value) } },
                label = if (isInquiry) strings.inquiryStatus else "Lead status",
                placeholder = "Hot, Warm, Cold"
            )
            HavnTextField(
                value = state.form.notes,
                onValueChange = { value -> viewModel.updateForm { copy(notes = value) } },
                label = "Agent notes",
                placeholder = "Motivation, preferences, timing, objections",
                singleLine = false
            )
            HavnTextField(
                value = state.form.dueAt,
                onValueChange = { value -> viewModel.updateForm { copy(dueAt = value) } },
                label = "Draft due time",
                placeholder = "2026-05-20T09:00:00Z",
                leadingIcon = HavnIconKey.Calendar
            )
            HavnTextField(
                value = state.form.completedAt,
                onValueChange = { value -> viewModel.updateForm { copy(completedAt = value) } },
                label = "Sent time",
                placeholder = "2026-05-19T12:00:00Z",
                leadingIcon = HavnIconKey.Check
            )
            HavnButton(
                text = "Generate drafts",
                onClick = viewModel::generateDraft,
                loading = state.isGenerating,
                modifier = Modifier.fillMaxWidth(),
                leadingIcon = HavnIconKey.Mail
            )
        }
    }
}

@Composable
fun FollowUpPreviewScreen(
    state: FollowUpsUiState,
    viewModel: FollowUpsViewModel
) {
    val theme = LocalHavnThemeTokens.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text("Draft type", color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                ChannelButton(
                    text = "SMS",
                    selected = state.previewChannel == FollowUpChannel.Sms,
                    onClick = { viewModel.selectPreviewChannel(FollowUpChannel.Sms) },
                    modifier = Modifier.weight(1f)
                )
                ChannelButton(
                    text = "Email",
                    selected = state.previewChannel == FollowUpChannel.Email,
                    onClick = { viewModel.selectPreviewChannel(FollowUpChannel.Email) },
                    modifier = Modifier.weight(1f)
                )
            }
            HavnTextField(
                value = state.selectedPreviewBody,
                onValueChange = viewModel::updatePreviewBody,
                label = if (state.previewChannel == FollowUpChannel.Email) "Email draft" else "SMS draft",
                singleLine = false
            )
            HavnButton(
                text = "Save draft",
                onClick = viewModel::saveSelectedDraft,
                loading = state.isSaving,
                modifier = Modifier.fillMaxWidth(),
                leadingIcon = HavnIconKey.Check
            )
            HavnButton(
                text = "Mark saved draft sent",
                onClick = viewModel::markSavedDraftSent,
                enabled = state.savedFollowUpId != null,
                loading = state.isSaving,
                style = HavnButtonStyle.Secondary,
                modifier = Modifier.fillMaxWidth()
            )
        }
    }
}

@Composable
private fun LeadPickerRow(
    lead: Lead,
    selected: Boolean,
    onClick: () -> Unit
) {
    HavnButton(
        text = "${lead.fullName} · ${lead.sourceProperty.title}",
        onClick = onClick,
        style = if (selected) HavnButtonStyle.Primary else HavnButtonStyle.Secondary,
        modifier = Modifier.fillMaxWidth(),
        leadingIcon = HavnIconKey.User
    )
}

@Composable
private fun FollowUpQueueRow(
    followUp: FollowUp,
    onMarkSent: () -> Unit,
    enabled: Boolean
) {
    val theme = LocalHavnThemeTokens.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Row(horizontalArrangement = Arrangement.spacedBy(12.dp), modifier = Modifier.fillMaxWidth()) {
                Column(Modifier.weight(1f)) {
                    Text(followUp.subject, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
                    Text(
                        "${followUp.channel.label} · ${followUp.status.label} · due ${followUp.dueAt.compactDateTime()}",
                        color = theme.primaryAccent,
                        style = MaterialTheme.typography.labelLarge
                    )
                }
            }
            followUp.body?.takeIf { it.isNotBlank() }?.let {
                Text(it, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium, maxLines = 3, overflow = TextOverflow.Ellipsis)
            }
            if (followUp.status != FollowUpStatus.Completed) {
                HavnButton(
                    text = "Mark sent",
                    onClick = onMarkSent,
                    enabled = enabled,
                    style = HavnButtonStyle.Secondary,
                    modifier = Modifier.fillMaxWidth()
                )
            }
        }
    }
}

@Composable
private fun ChannelButton(
    text: String,
    selected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    HavnButton(
        text = text,
        onClick = onClick,
        style = if (selected) HavnButtonStyle.Primary else HavnButtonStyle.Secondary,
        modifier = modifier
    )
}

private fun String.compactDateTime(): String {
    if (isBlank()) return "not scheduled"
    val clean = replace("T", " ").substringBefore(".").substringBefore("+").substringBefore("Z")
    val date = clean.substringBefore(" ").readableFollowUpDate()
    val time = clean.substringAfter(" ", "").readableFollowUpTime()
    return listOfNotNull(date, time).joinToString(" · ").ifBlank { clean }
}

private fun String.readableFollowUpDate(): String? {
    val parts = split("-")
    if (parts.size != 3) return null
    val month = parts[1].toIntOrNull()?.let { followUpMonthNames.getOrNull(it - 1) } ?: return null
    val day = parts[2].toIntOrNull() ?: return null
    return "$month $day"
}

private fun String.readableFollowUpTime(): String? {
    if (":" !in this) return null
    val hour = substringBefore(":").toIntOrNull() ?: return null
    val minute = substringAfter(":").take(2).padStart(2, '0')
    val displayHour = when {
        hour == 0 -> 12
        hour > 12 -> hour - 12
        else -> hour
    }
    val suffix = if (hour >= 12) "PM" else "AM"
    return "$displayHour:$minute $suffix"
}

private val followUpMonthNames = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")

private fun FollowUpsMode.navigationDepth(): Int = when (this) {
    FollowUpsMode.Queue -> 0
    FollowUpsMode.Generate -> 1
    FollowUpsMode.Preview -> 2
}

private fun HavnAttachment.sizeLabel(): String = when {
    sizeBytes >= 1024L * 1024L -> "${(sizeBytes / (1024.0 * 1024.0)).roundOneDecimal()} MB"
    sizeBytes >= 1024L -> "${(sizeBytes / 1024.0).roundOneDecimal()} KB"
    else -> "$sizeBytes B"
}

private fun Double.roundOneDecimal(): String {
    val rounded = kotlin.math.round(this * 10.0) / 10.0
    val text = rounded.toString()
    return if (text.endsWith(".0")) text.dropLast(2) else text
}
